import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient, QueryObserver } from '@tanstack/react-query'
import { GridApi, RowGroupOpenedEvent } from 'ag-grid-enterprise'
import { EnhancedStatementRow } from '../utils/dataEnhancement'
import { financialStatementsKeys } from '../queries/financialStatementsQueryKeys'

interface ObserverConfig {
  menuNodeId: string
  queryKey: readonly unknown[]
  observer: QueryObserver<any>
  unsubscribe: () => void
}

/**
 * Generic hook for async data loading in AG Grid using QueryObserver pattern
 *
 * When a menu row is expanded:
 * 1. Creates a QueryObserver with queryKey + queryFn
 * 2. Observer automatically fetches data and updates on invalidation
 * 3. Subscribe callback updates grid with new data
 *
 * When a menu row is collapsed:
 * 1. Unsubscribes and removes observer
 * 2. Child rows are removed by AG Grid automatically
 */
export const useAsyncGridData = (
  gridApi: GridApi<EnhancedStatementRow> | undefined,
  companyId: string | undefined,
  budgetingScenarioId: number | null | undefined,
  periodGroups?: any[]
) => {
  const queryClient = useQueryClient()
  const observersRef = useRef<Map<string, ObserverConfig>>(new Map())

  // Cleanup all observers on unmount or when key parameters change
  // This prevents duplicate observers during hot reload
  useEffect(() => {
    return () => {
      observersRef.current.forEach(config => config.unsubscribe())
      observersRef.current.clear()
    }
  }, [companyId, budgetingScenarioId, periodGroups])

  const onRowGroupOpened = useCallback(
    async (event: RowGroupOpenedEvent<EnhancedStatementRow>) => {
      const { node } = event
      const rowData = node.data

      // Only handle rows with asyncConfig
      if (!rowData?.asyncConfig || !companyId || !gridApi) return

      const { getQueryKey, fetchData } = rowData.asyncConfig

      // Get query key
      const queryKey = await Promise.resolve(
        getQueryKey({
          accountCode: rowData._sourceAccountCode,
          budgetingScenarioId,
          statementRowId: rowData._sourceStatementRowId
        })
      )

      const queryKeyString = JSON.stringify(queryKey)

      // ROW COLLAPSED - cleanup observer
      if (!node.expanded) {
        const config = observersRef.current.get(queryKeyString)
        if (config) {
          config.unsubscribe()
          observersRef.current.delete(queryKeyString)
        }
        return
      }

      // ROW EXPANDED - check if observer already exists
      if (observersRef.current.has(queryKeyString)) {
        return // Observer already exists, nothing to do
      }

      // Create new QueryObserver
      const observer = new QueryObserver(queryClient, {
        queryKey,
        queryFn: () =>
          fetchData({
            companyId,
            accountCode: rowData._sourceAccountCode,
            budgetingScenarioId,
            periodGroups,
            statementRowId: rowData._sourceStatementRowId
          }),
        staleTime: 0, // Always refetch to get latest data
        gcTime: 0 // Don't cache results
      })

      // Subscribe to query updates
      const unsubscribe = observer.subscribe(result => {
        // Find menu node for loading state updates
        const menuNode = gridApi.getRenderedNodes().find(n => n.id === node.id)
        if (!menuNode?.data?.asyncConfig) return

        // Update loading state
        if (result.isFetching && menuNode.data) {
          menuNode.setData({ ...menuNode.data, isLoading: true })
          return
        }

        // Only process successful results with data (and not currently fetching)
        if (result.status !== 'success' || !result.data || result.isFetching) return

        const config = menuNode.data.asyncConfig

        // Clear loading state
        if (menuNode.data) {
          menuNode.setData({ ...menuNode.data, isLoading: false })
        }

        // Skip if menu is collapsed AND there's no data (empty state)
        // But if there's data, we should expand and show it automatically
        if (!menuNode.expanded && Array.isArray(result.data) && result.data.length === 0) {
          return
        }

        // Find existing child rows for this menu node
        const existingRowsMap = new Map<string, EnhancedStatementRow>()
        const menuPath = menuNode.data.path

        gridApi.forEachNode(gridNode => {
          if (!gridNode.data || !gridNode.data.path) return

          const nodeData = gridNode.data
          const nodePath = nodeData.path

          // Check if this row is a child of the menu node
          // A child row's path starts with the menu node's path
          const isChild =
            nodePath.length > menuPath.length &&
            menuPath.every((segment, index) => nodePath[index] === segment) &&
            nodeData.rowType === config.dataRowType

          if (isChild) {
            existingRowsMap.set(nodeData.key, nodeData)
          }
        })

        // Sort and transform new data
        let data = result.data as any[]
        // console.log('data', data, menuNode.data)
        if (config.sortData) {
          data = [...data].sort(config.sortData)
        }

        const enhancedRows = config.transformData(data, menuNode.data)

        // Calculate diff between existing and new rows
        const toAdd: EnhancedStatementRow[] = []
        const toUpdate: EnhancedStatementRow[] = []
        const toRemove: EnhancedStatementRow[] = []

        const newRowsMap = new Map<string, EnhancedStatementRow>()
        enhancedRows.forEach(row => {
          newRowsMap.set(row.key, row)
        })

        // Find added and updated rows
        enhancedRows.forEach(newRow => {
          const existingRow = existingRowsMap.get(newRow.key)

          if (!existingRow) {
            // New row
            toAdd.push(newRow)
          } else if (true) {
            // Updated row
            toUpdate.push(newRow)
          }
        })

        // Find removed rows
        existingRowsMap.forEach((existingRow, key) => {
          if (!newRowsMap.has(key)) {
            toRemove.push(existingRow)
          }
        })

        // Apply transaction with only the changes
        if (toAdd.length > 0 || toUpdate.length > 0 || toRemove.length > 0) {
          gridApi.applyTransaction({
            add: toAdd.length > 0 ? toAdd : undefined,
            update: toUpdate.length > 0 ? toUpdate : undefined,
            remove: toRemove.length > 0 ? toRemove : undefined
          })

          // Invalidate financial statements when async data changes
          // This ensures the main report stays in sync with dimension/detail changes
          if (toUpdate.length > 0 || toRemove.length > 0 || toAdd.length > 0) {
            queryClient
              .invalidateQueries({
                queryKey: financialStatementsKeys.statements()
              })
              .catch(error => {
                console.error('Error invalidating financial statements after async data change:', error)
              })
          }
        }

        // Ensure menu node stays expanded after updating
        gridApi.setRowNodeExpanded(menuNode, true)
      })

      // Store observer
      observersRef.current.set(queryKeyString, {
        menuNodeId: node.id!,
        queryKey,
        observer,
        unsubscribe
      })
    },
    [gridApi, companyId, budgetingScenarioId, periodGroups, queryClient]
  )

  return onRowGroupOpened
}
